import {
    useCallback,
    useMemo,
    useState,
    type Dispatch,
    type SetStateAction,
} from "react";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import { FiltersTypes } from "@common/types/filters/general";
import { View } from "@common/types/views/views";
import type { EntityProfileType } from "@common/types/entity";
import { FiltersChangeFn } from "@components/pageHeader/filters/lib";
import { entityProfileTypeLabels } from "@components/entity/constants";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import type { ApplyCoaTemplateEntitySelectionRow } from "./applyCoaTemplateEntitySelection.types";
import { applyCoaTemplateEntitySelectionViewConfig } from "./applyCoaTemplateEntitySelectionViewConfig";
import { defaultApplyCoaEntitySelectionView } from "./applyCoaTemplateEntitySelectionView.constants";
import { ApplyCoaTemplateSelectEntitiesToolbar } from "./ApplyCoaTemplateSelectEntitiesToolbar";
import { ApplyCoaTemplateSelectEntitiesFooter } from "./ApplyCoaTemplateSelectEntitiesFooter";
import {
    filterSelectEntitiesRows,
    filterWorkspaceRowsByServicesAndTeam,
} from "./applyCoaTemplateSelectEntitiesFilters";
import styles from "./ApplyCoaTemplateDrawer.module.scss";

interface Props {
    tableData: ApplyCoaTemplateEntitySelectionRow[];
    applicableEntityIds: Set<string>;
    entityLegalType: EntityProfileType | undefined;
    isLoading: boolean;
    isError: boolean;
    selectedEntityIds: Set<string>;
    setSelectedEntityIds: Dispatch<SetStateAction<Set<string>>>;
    selectedApplicableEntityIds: string[];
    onContinue: () => void;
    onAddEntity: () => void;
    onEditTemplate: () => void;
}

export const ApplyCoaTemplateSelectEntitiesStep = ({
    tableData,
    applicableEntityIds,
    entityLegalType,
    isLoading,
    isError,
    selectedEntityIds,
    setSelectedEntityIds,
    selectedApplicableEntityIds,
    onContinue,
    onAddEntity,
    onEditTemplate,
}: Props) => {
    const [view, setView] = useState<View>(defaultApplyCoaEntitySelectionView);
    const [searchFilter, setSearchFilter] = useState<string | undefined>(
        undefined,
    );
    const [filters, setFilters] = useState<FiltersTypes>({});

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (patch) => setFilters((current) => ({ ...current, ...patch })),
        [],
    );

    // Filter the workspace → entity tree by client/entity name plus the
    // client-level Services / Team filters and the entity-level Industry / Basis
    // filters. Services and Team are client-level, so a non-matching client
    // drops its whole workspace + entities. Name search, Industry and Basis are
    // resolved per child; workspaces with zero remaining entities are hidden.
    const filteredTableData = useMemo<
        ApplyCoaTemplateEntitySelectionRow[]
    >(() => {
        const selectedServices = filters.services ?? [];
        const selectedTeamMemberIds = filters.teamMemberIds ?? [];
        const selectedIndustries = filters.entityIndustries ?? [];
        const selectedBases = filters.entityBases ?? [];
        const hasServiceFilter = selectedServices.length > 0;
        const hasTeamFilter = selectedTeamMemberIds.length > 0;
        const hasSearch = !!searchFilter?.trim();
        const hasIndustryFilter = selectedIndustries.length > 0;
        const hasBasisFilter = selectedBases.length > 0;

        if (
            !hasSearch &&
            !hasServiceFilter &&
            !hasTeamFilter &&
            !hasIndustryFilter &&
            !hasBasisFilter
        ) {
            return tableData;
        }

        // Workspace-level Services / Team filter drops the entire workspace.
        const serviceTeamFiltered = filterWorkspaceRowsByServicesAndTeam({
            rows: tableData,
            selectedServices,
            selectedTeamMemberIds,
        });

        return filterSelectEntitiesRows({
            rows: serviceTeamFiltered,
            search: searchFilter,
            selectedIndustries,
            selectedBases,
        });
    }, [tableData, searchFilter, filters]);

    // Applicable entity ids currently visible under the active search/filters.
    // Select-all and the footer counts are scoped to this set so they reflect
    // what the user can actually see, not the whole org.
    const filteredApplicableEntityIds = useMemo<Set<string>>(
        () =>
            new Set(
                filteredTableData.flatMap(
                    (workspaceRow) =>
                        workspaceRow.children
                            ?.map((child) => child.id)
                            .filter((id) => applicableEntityIds.has(id)) ?? [],
                ),
            ),
        [filteredTableData, applicableEntityIds],
    );

    // Controlled TanStack selection derived from the single source of truth
    // (selectedEntityIds), scoped to the *visible* rows. A workspace row is
    // marked selected only when all of its visible applicable entities are
    // selected, so its checkbox reflects the filtered children.
    const rowSelection = useMemo<RowSelectionState>(() => {
        const selection: RowSelectionState = {};
        for (const workspaceRow of filteredTableData) {
            const childIds =
                workspaceRow.children?.map((child) => child.id) ?? [];
            for (const childId of childIds) {
                if (selectedEntityIds.has(childId)) {
                    selection[childId] = true;
                }
            }
            if (
                childIds.length > 0 &&
                childIds.every((id) => selectedEntityIds.has(id))
            ) {
                selection[workspaceRow.id] = true;
            }
        }
        return selection;
    }, [filteredTableData, selectedEntityIds]);

    // Translate any TanStack selection change back into entity (child) ids only
    // — workspace ids never leave the step. Selections of rows hidden by the
    // current filter are preserved; only the visible applicable rows are
    // replaced by the table's new state.
    const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
        (updater) => {
            const next =
                typeof updater === "function" ? updater(rowSelection) : updater;
            const visibleSelectedIds = Object.keys(next).filter(
                (id) => next[id] && filteredApplicableEntityIds.has(id),
            );
            setSelectedEntityIds((previous) => {
                const merged = new Set(
                    [...previous].filter(
                        (id) => !filteredApplicableEntityIds.has(id),
                    ),
                );
                for (const id of visibleSelectedIds) {
                    merged.add(id);
                }
                return merged;
            });
        },
        [rowSelection, filteredApplicableEntityIds, setSelectedEntityIds],
    );

    const handleUnselectAll = useCallback(() => {
        setSelectedEntityIds(new Set());
    }, [setSelectedEntityIds]);

    const emptyStateBody = useMemo(() => {
        const entityTypeLabel = entityLegalType
            ? entityProfileTypeLabels[entityLegalType]
            : undefined;
        return entityTypeLabel
            ? `This template applies to ${entityTypeLabel} entities only. Add an entity with that type to apply it.`
            : "Add an entity that matches this template to apply it.";
    }, [entityLegalType]);

    if (isLoading) {
        return (
            <div className={styles.stepRoot}>
                <div className={styles.subtitleContainer}>
                    Step 1 of 2 · Select entities
                </div>
                <Loader />
            </div>
        );
    }

    if (isError) {
        return (
            <div className={styles.stepRoot}>
                <div className={styles.subtitleContainer}>
                    Step 1 of 2 · Select entities
                </div>
                <EmptyState header="Couldn't load entities. Please try again." />
            </div>
        );
    }

    return (
        <div className={styles.stepRoot}>
            <div className={styles.subtitleContainer}>
                Step 1 of 2 · Select entities
            </div>
            <ApplyCoaTemplateSelectEntitiesToolbar
                searchFilter={searchFilter}
                onSearchChange={setSearchFilter}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                view={view}
                onViewChange={setView}
            />
            <ViewTable
                data={filteredTableData}
                view={view}
                config={applyCoaTemplateEntitySelectionViewConfig}
                onViewChange={setView}
                rowSelection={rowSelection}
                onRowSelectionChange={handleRowSelectionChange}
                enableColumnResizing
                autoExpandOnMount
                emptyView={() => (
                    <EmptyState
                        header="No matching entities yet"
                        body={emptyStateBody}
                        ctaText="Add entity"
                        action={onAddEntity}
                        secondaryCtaText="Edit template"
                        secondaryAction={onEditTemplate}
                    />
                )}
                footer={
                    <ApplyCoaTemplateSelectEntitiesFooter
                        selectedCount={selectedApplicableEntityIds.length}
                        onUnselectAll={handleUnselectAll}
                        onContinue={onContinue}
                    />
                }
            />
        </div>
    );
};
